import { Box, Flex, Spinner, Text } from "@/ui";
import Icon from "@/components/Icon";
import { t } from "@/utils";
import { useMonitoringPanelButtonStyle } from "@/theme/utils/buttonStyleHooks";
import { autoHideStyle } from "./videoUIAutoHide";
import PttButton from "./PttButton";

type ItemProps = {
  key: CameraControlKey;
  icon: any;
  labelKey: StringKey;
  isProcessing: boolean;
  isDisabled: boolean;
  action?: Cb;
  Component?: ({ actionController }: { actionController: ActionController }) => React.ReactNode | null;
  isHidden?: boolean;
};

type Props = {
  items: (ItemProps | null)[];
  actionController: ActionController;
  actionStates: ActionStates;
};

const Item = ({
  buttonHoverBg,
  buttonActiveBg,
  bg,
  isProcessing,
  isDisabled,
  icon,
  labelKey,
  isHidden,
  action
}: Omit<ItemProps, "key"> & {
  buttonHoverBg: string;
  buttonActiveBg: string;
  bg: string;
  isHidden?: boolean;
}) => {
  const label = t(labelKey);
  const isMultiWorded = label.includes(" ");
  return (
    <Flex
      direction="column"
      align="center"
      as="button"
      my="0.5rem"
      disabled={isDisabled}
      maxW="56px"
      borderRadius="12px"
      opacity={isHidden ? 0 : 1}
      pointerEvents={isHidden ? "none" : "auto"}
      _focusVisible={{
        outline: "2px solid",
        outlineColor: "primary"
      }}
      _hover={{ ".icn-bg": { bg: buttonHoverBg } }}
      _active={{ ".icn-bg": { bg: buttonActiveBg } }}
      _disabled={{
        opacity: 0.4,
        boxShadow: "none",
        pointerEvents: "none"
      }}
      onClick={action}
    >
      <Flex p="12px" bg={bg} borderRadius="12px" className="icn-bg" transition="all 0.3s" backdropFilter="blur(8px)">
        {isProcessing ? <Spinner size="md" borderWidth="3px" /> : <Icon icon={icon} fill="neutral.99" />}
      </Flex>
      <Text
        mt="4px"
        variant="label2"
        color="primary.99"
        maxW="100%"
        display="-webkit-box"
        lineHeight="14px"
        textOverflow="ellipsis"
        overflow="hidden"
        sx={{
          WebkitLineClamp: isMultiWorded ? 2 : 1,
          WebkitBoxOrient: "vertical"
        }}
      >
        {label}
      </Text>
    </Flex>
  );
};

export default function BottomPanel({ items, actionController, actionStates }: Props) {
  const { getStyleObjAsValues } = useMonitoringPanelButtonStyle();

  const leftActions = items.slice(0, 2);
  const rightActions = items.slice(2);

  return (
    <Box pos="absolute" left="50%" bottom="12" transform="translateX(-50%)" {...autoHideStyle}>
      <Flex align="flex-start" columnGap="1rem">
        {leftActions.map((item) => {
          if (!item) return null;
          const { icon, key, labelKey, action, Component } = item;
          const isActive = actionStates[key].isActive;
          const isDisabled = actionStates[key].isDisabled;
          const isProcessing = actionStates[key].isProcessing;
          const isHidden = key === "record" && actionStates[key].isActive;

          const { activeBg, bg, hoverBg } = getStyleObjAsValues(isActive);
          return (
            <Box key={key} pos="relative">
              {Component && <Component actionController={actionController} />}
              <Item
                bg={bg}
                isDisabled={isDisabled}
                isProcessing={isProcessing}
                buttonActiveBg={activeBg}
                buttonHoverBg={hoverBg}
                icon={icon}
                labelKey={labelKey}
                action={action}
                isHidden={isHidden}
              />
            </Box>
          );
        })}
        <PttButton />
        {rightActions.map((item) => {
          if (!item) return null;
          const { icon, key, labelKey, action, Component } = item;
          const isActive = actionStates[key].isActive;
          const isDisabled = actionStates[key].isDisabled;
          const isProcessing = actionStates[key].isProcessing;
          const isHidden = key === "record" && actionStates[key].isActive;

          const { activeBg, bg, hoverBg } = getStyleObjAsValues(isActive);
          return (
            <Box key={key} pos="relative">
              {Component && <Component actionController={actionController} />}
              <Item
                bg={bg}
                isDisabled={isDisabled}
                isProcessing={isProcessing}
                buttonActiveBg={activeBg}
                buttonHoverBg={hoverBg}
                icon={icon}
                labelKey={labelKey}
                action={action}
                isHidden={isHidden}
              />
            </Box>
          );
        })}
      </Flex>
    </Box>
  );
}
